import loadable from '@loadable/component';

import If from '@components/If';

import { getFill } from './getFill';

import type { BriefType } from './types';
import { BriefOutcome } from './types';

import { Filler } from './styled';

const ForecastDowngradeIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ForecastDowngradeOutline')
);
const ForecastUpgradeIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ForecastUpgradeOutline')
);
const ChartDownIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ChartDownOutline')
);
const ChartUpIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ChartUpOutline')
);
const DividendIcon = loadable(
  () => import('@simplywallst/ui-core/icons/DividendOutline')
);
const EarningsReportIcon = loadable(
  () => import('@simplywallst/ui-core/icons/EarningReportOutline')
);
const InsiderUpIcon = loadable(
  () => import('@simplywallst/ui-core/icons/InsiderUpOutline')
);
const InsiderDownIcon = loadable(
  () => import('@simplywallst/ui-core/icons/InsiderDownOutline')
);
const PriceUpIcon = loadable(
  () => import('@simplywallst/ui-core/icons/PriceUpOutline')
);
const PriceDownIcon = loadable(
  () => import('@simplywallst/ui-core/icons/PriceDownOutline')
);
const ForecastIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ForecastOutline')
);
const PEIcon = loadable(() => import('@simplywallst/ui-core/icons/PeOutline'));
const BreakEvenIcon = loadable(
  () => import('@simplywallst/ui-core/icons/BreakEvenOutline')
);
const ExecutiveLeavingIcon = loadable(
  () => import('@simplywallst/ui-core/icons/ExecutiveLeavingOutline')
);
const BoardChange = loadable(
  () => import('@simplywallst/ui-core/icons/BoardChangeOutline')
);
const DirectorOverboarding = loadable(
  () => import('@simplywallst/ui-core/icons/ExecutiveOverboardingOutline')
);
const BuyingOpportunity = loadable(
  () => import('@simplywallst/ui-core/icons/ValuationOutline')
);
const HasNewRisk = loadable(
  () => import('@simplywallst/ui-core/icons/NewRiskOutline')
);

export interface Props {
  id: string;
  outcome?: number | null;
}

const HasPriceTargetChanged = ({ outcome }: Pick<Props, 'outcome'>) =>
  outcome === BriefOutcome.OUTCOME_TRUE_REVISION_UP ? (
    <PriceUpIcon data-cy-id="dashboard-brief-price-up" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_REVISION_DOWN ? (
    <PriceDownIcon data-cy-id="dashboard-brief-price-down" />
  ) : null;

const IsNew90DayHighLow = ({ outcome }: Pick<Props, 'outcome'>) =>
  outcome === BriefOutcome.OUTCOME_TRUE_90D_HIGH ? (
    <ChartUpIcon data-cy-id="dashboard-brief-chart-up" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_90D_LOW ? (
    <ChartDownIcon data-cy-id="dashboard-brief-chart-down" />
  ) : null;

const InsiderTransactions = ({ outcome }: Pick<Props, 'outcome'>) =>
  outcome === BriefOutcome.OUTCOME_TRUE_INSIDER_BUY ? (
    <InsiderUpIcon data-cy-id="dashboard-brief-insider-up" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_INSIDER_SELL ? (
    <InsiderDownIcon data-cy-id="dashboard-brief-insider-down" />
  ) : null;

const HasMajorEstimateRevision = ({ outcome }: Pick<Props, 'outcome'>) =>
  outcome === BriefOutcome.OUTCOME_TRUE_REVENUE_UPGRADE ? (
    <ForecastUpgradeIcon data-cy-id="dashboard-brief-forecast-upgrade" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_EPS_UPGRADE ? (
    <ForecastUpgradeIcon data-cy-id="dashboard-brief-forecast-upgrade" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_REVENUE_DOWNGRADE ? (
    <ForecastDowngradeIcon data-cy-id="dashboard-brief-forecast-downgrade" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_EPS_DOWNGRADE ? (
    <ForecastDowngradeIcon data-cy-id="dashboard-brief-forecast-downgrade" />
  ) : outcome === BriefOutcome.OUTCOME_TRUE_MIXED_REVISION ? (
    <ForecastIcon data-cy-id="dashboard-brief-forecast-neutral" />
  ) : null;

const BriefIcon = ({ id, outcome }: Props) => {
  const safeId: BriefType = JSON.parse(id)[0] || undefined;
  const fill = getFill(id, outcome);
  return (
    <Filler fill={fill}>
      <If
        conditions={{
          hasPriceTargetChanged: safeId === 'HasPriceTargetChanged',
          isNew90DayHighLow: safeId === 'IsNew90DayHighLow',
          hasRecentInsiderTransactions:
            safeId === 'HasRecentInsiderTransactions' ||
            safeId === 'HasRecentInsiderTransactionsDerivative',
          hasMajorEstimateRevision: safeId === 'HasMajorEstimateRevision',
          hasUpcomingDividend: safeId === 'HasUpcomingDividend',
          hasReportedEarnings:
            safeId === 'HasReportedEarnings' ||
            safeId === 'AnalystEstimateSurprisePostEarnings',
          valuationUpdateWith7DayPriceMove:
            safeId === 'ValuationUpdateWith7DayPriceMove',
          hasExecutiveDeparture: safeId === 'HasExecutiveDeparture',
          hasBreakevenDateChange: safeId === 'HasBreakevenDateChange',
          boardChange: safeId === 'BoardChange',
          hasDirectoryOverboarding: safeId === 'HasDirectorOverboarding',
          buyOrSellOpportunity: safeId === 'BuyOrSellOpportunity',
          hasNewRisk: safeId === 'HasNewRisk',
          hasDeclaredDividend: safeId === 'HasDeclaredDividend',
          buyingOpportunity: safeId === 'BuyingOpportunity',
        }}
      >
        {{
          hasPriceTargetChanged: () => (
            <HasPriceTargetChanged outcome={outcome} />
          ),
          isNew90DayHighLow: () => <IsNew90DayHighLow outcome={outcome} />,
          hasRecentInsiderTransactions: () => (
            <InsiderTransactions outcome={outcome} />
          ),
          hasMajorEstimateRevision: () => (
            <HasMajorEstimateRevision outcome={outcome} />
          ),
          hasUpcomingDividend: () => (
            <DividendIcon data-cy-id="dashboard-brief-dividend" />
          ),
          hasReportedEarnings: () => (
            <EarningsReportIcon data-cy-id="dashboard-brief-earnings" />
          ),
          valuationUpdateWith7DayPriceMove: () => (
            <PEIcon data-cy-id="dashboard-brief-PE" />
          ),
          hasExecutiveDeparture: () => (
            <ExecutiveLeavingIcon data-cy-id="dashboard-executive-leaving" />
          ),
          hasBreakevenDateChange: () => (
            <BreakEvenIcon data-cy-id="dashboard-break-even" />
          ),
          boardChange: () => (
            <BoardChange data-cy-id="dashboard-board-change" />
          ),
          hasDirectoryOverboarding: () => (
            <DirectorOverboarding data-cy-id="dashboard-director-overboarding" />
          ),
          buyOrSellOpportunity: () => (
            <BuyingOpportunity data-cy-id="dashboard-buy-or-sell-opportunity" />
          ),
          hasNewRisk: () => <HasNewRisk data-cy-id="dashboard-has-new-risk" />,
          hasDeclaredDividend: () => (
            <DividendIcon data-cy-id="dashboard-has-declared-dividend" />
          ),
          buyingOpportunity: () => (
            <BuyingOpportunity data-cy-id="dashboard-buying-opportunity" />
          ),
        }}
      </If>
    </Filler>
  );
};

export default BriefIcon;
